// File: src/utils/packageCache.js
// Last modified: 2025-10-29

/**
 * Package Cache Utility
 * Caches package data to avoid unnecessary API calls
 * Uses localStorage for persistence across sessions
 */

const CACHE_PREFIX = 'zakovat_package_';
const CACHE_EXPIRY_KEY = 'zakovat_cache_expiry_';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

// Guards against caching/serving a still-encrypted envelope ({d, iv}) or any other
// malformed payload (e.g. from a decrypt failure) as if it were real package data.
function isValidPackageData(data) {
    return !!data && typeof data.id === 'string' && Array.isArray(data.questions);
}

class PackageCache {
    constructor() {
        this.memoryCache = new Map();
    }

    /**
     * Generate cache key for a package
     */
    getCacheKey(packageId) {
        return `${CACHE_PREFIX}${packageId}`;
    }

    /**
     * Generate expiry key for a package
     */
    getExpiryKey(packageId) {
        return `${CACHE_EXPIRY_KEY}${packageId}`;
    }

    /**
     * Check if cache entry is expired
     */
    isExpired(packageId) {
        const expiryKey = this.getExpiryKey(packageId);
        const expiry = localStorage.getItem(expiryKey);

        if (!expiry) return true;

        return Date.now() > parseInt(expiry);
    }

    /**
     * Get package from cache
     * First checks memory cache, then localStorage
     */
    get(packageId) {
        // Check memory cache first
        if (this.memoryCache.has(packageId)) {
            const memoryData = this.memoryCache.get(packageId);
            if (!isValidPackageData(memoryData)) {
                console.warn(`[Cache] Discarding corrupted memory entry for package ${packageId}`);
                this.remove(packageId);
                return null;
            }
            console.log(`[Cache] Memory hit for package ${packageId}`);
            return memoryData;
        }

        // Check if localStorage cache exists and is not expired
        if (this.isExpired(packageId)) {
            console.log(`[Cache] Expired for package ${packageId}`);
            this.remove(packageId);
            return null;
        }

        // Try to get from localStorage
        try {
            const cacheKey = this.getCacheKey(packageId);
            const cached = localStorage.getItem(cacheKey);

            if (cached) {
                const packageData = JSON.parse(cached);
                if (!isValidPackageData(packageData)) {
                    console.warn(`[Cache] Discarding corrupted localStorage entry for package ${packageId}`);
                    this.remove(packageId);
                    return null;
                }
                // Store in memory cache for faster access
                this.memoryCache.set(packageId, packageData);
                console.log(`[Cache] LocalStorage hit for package ${packageId}`);
                return packageData;
            }
        } catch (error) {
            console.error('[Cache] Error reading from localStorage:', error);
            this.remove(packageId);
        }

        return null;
    }

    /**
     * Set package in cache
     * Stores in both memory and localStorage
     */
    set(packageId, packageData) {
        if (!isValidPackageData(packageData)) {
            console.warn(`[Cache] Refusing to cache malformed data for package ${packageId}`);
            return;
        }

        try {
            // Store in memory cache
            this.memoryCache.set(packageId, packageData);

            // Store in localStorage with expiry
            const cacheKey = this.getCacheKey(packageId);
            const expiryKey = this.getExpiryKey(packageId);
            const expiry = Date.now() + CACHE_DURATION;

            localStorage.setItem(cacheKey, JSON.stringify(packageData));
            localStorage.setItem(expiryKey, expiry.toString());

            console.log(`[Cache] Stored package ${packageId}, expires in ${CACHE_DURATION / 60000} minutes`);
        } catch (error) {
            console.error('[Cache] Error writing to localStorage:', error);
            // If localStorage is full, just keep in memory
            if (error.name === 'QuotaExceededError') {
                console.warn('[Cache] LocalStorage quota exceeded, using memory only');
            }
        }
    }

    /**
     * Remove package from cache
     */
    remove(packageId) {
        this.memoryCache.delete(packageId);

        try {
            const cacheKey = this.getCacheKey(packageId);
            const expiryKey = this.getExpiryKey(packageId);
            localStorage.removeItem(cacheKey);
            localStorage.removeItem(expiryKey);
            console.log(`[Cache] Removed package ${packageId}`);
        } catch (error) {
            console.error('[Cache] Error removing from localStorage:', error);
        }
    }

    /**
     * Clear all package caches
     */
    clearAll() {
        this.memoryCache.clear();

        try {
            // Remove all zakovat package cache entries
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith(CACHE_PREFIX) || key.startsWith(CACHE_EXPIRY_KEY)) {
                    localStorage.removeItem(key);
                }
            });
            console.log('[Cache] Cleared all caches');
        } catch (error) {
            console.error('[Cache] Error clearing localStorage:', error);
        }
    }

    /**
     * Update a specific question's saved status in cache
     */
    updateQuestionSavedStatus(packageId, questionId, saved) {
        const packageData = this.get(packageId);
        if (packageData && packageData.questions) {
            const question = packageData.questions.find(q => q.id === questionId);
            if (question) {
                question.saved = saved;
                this.set(packageId, packageData);
                console.log(`[Cache] Updated question ${questionId} saved status to ${saved}`);
            }
        }
    }

    /**
     * Get cache statistics
     */
    getStats() {
        return {
            memorySize: this.memoryCache.size,
            localStorageKeys: Object.keys(localStorage).filter(key =>
                key.startsWith(CACHE_PREFIX)
            ).length
        };
    }
}

// Export singleton instance
const packageCache = new PackageCache();
export default packageCache;
